import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { activationFormFromSummary, activationFormDirty, activationProfilePayload } from './activationProfileForm';
import {
  getPlayerAccountEmail, updateRosterPlayer, createRosterActivationLink,
  sendPlayerResetLink, sendPlayerLoginLink, requestPlayerLoginEmailChange, requestVerifyLoginEmail,
} from '../../lib/api';

function userError(message) {
  const error = new Error(message);
  error.userMessage = message;
  return error;
}

export default function AccountEmailPanel({ teamId, profileId, onChanged, onDirtyChange, mode = 'account', showHeading = true, onManageEmail, onBusyChange }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [ownEmail, setOwnEmail] = useState('');
  const [candidate, setCandidate] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [activationForm, setActivationForm] = useState({ noOwnEmail: true, ownEmail: '', contactEmail: '' });
  const [activationUrl, setActivationUrl] = useState('');
  const [usernameDraft, setUsernameDraft] = useState('');
  const lock = useRef(false);
  const mounted = useRef(false);
  const serial = useRef(0);

  const load = useCallback(async () => {
    const request = ++serial.current;
    const res = await getPlayerAccountEmail(teamId, profileId);
    if (!mounted.current || request !== serial.current) return;
    setData(res.data);
    setActivationForm(activationFormFromSummary(res.data));
    setOwnEmail(res.data.profile_email || '');
    setCandidate(res.data.login_email || '');
    setError('');
  }, [teamId, profileId]);
  useEffect(() => {
    mounted.current = true;
    setData(null);
    setError('');
    load().catch(() => { if (mounted.current) setError('Kunde inte hämta aktuella adresser. Försök igen.'); });
    return () => { mounted.current = false; serial.current += 1; };
  }, [load]);

  const unsaved = Boolean(data && (!data.has_account ? activationFormDirty(activationForm, data) : data.login_email
    ? candidate.trim().toLowerCase() !== data.login_email.toLowerCase()
    : ownEmail.trim().toLowerCase() !== (data.profile_email || '')));
  useEffect(() => {
    onDirtyChange?.(unsaved);
    return () => onDirtyChange?.(false);
  }, [unsaved, onDirtyChange]);

  const run = async (action, changed = false) => {
    if (lock.current) return;
    lock.current = true;
    onBusyChange?.(true);
    setBusy(true);
    try {
      await action();
      await load();
      if (changed && mounted.current) onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (mounted.current) {
        setError(typeof detail === 'string' ? detail : err.userMessage || 'Åtgärden kunde inte bekräftas. Uppdatera uppgifterna och försök igen.');
      }
    } finally {
      lock.current = false;
      onBusyChange?.(false);
      if (mounted.current) setBusy(false);
    }
  };
  if (!teamId || !profileId) return <p className="text-sm text-destructive">Spelarens lag eller profil saknas.</p>;
  if (!data) return <div className="space-y-2 text-sm">
    <p>{error || 'Hämtar aktuella adresser…'}</p>
    {error && <Button type="button" variant="outline" disabled={busy} onClick={() => run(async () => {})}>Försök igen</Button>}
  </div>;

  const dirty = ownEmail.trim().toLowerCase() !== (data.profile_email || '');
  const loginDirty = candidate.trim().toLowerCase() !== (data.login_email || '').toLowerCase();
  const requestChange = () => run(async () => {
    const res = await requestPlayerLoginEmailChange(teamId, profileId, {
      candidate_email: candidate.trim(), recovery, reason: reason.trim(), identity_checked: checked,
    });
    if (res.data?.success !== true) throw new Error('Unconfirmed request');
    toast.success(recovery ? `Verifiering skickad till ${res.data.delivery_email}. Adressbytet väntar på bekräftelse.` : 'Verifiering skickad till gamla och nya adressen. Adressbytet väntar på bekräftelse.');
    if (mounted.current) { setReason(''); setChecked(false); setRecovery(false); }
  }, true);

  const copyMode = mode === 'activation-copy';
  const usesUsername = data.activation_login_mode === 'username';
  if (mode === 'activation' || copyMode) return <section className="space-y-4" data-testid="player-activation-panel">
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {data.has_account ? <>
      <p className="text-sm">Spelaren har redan ett konto. Använd E-post och inloggning för att få hjälp med inloggningen.</p>
      <Button type="button" variant="outline" disabled={busy} onClick={() => onManageEmail?.('account')}>E-post och inloggning</Button>
    </> : <>
      <div className="rounded-lg border border-border p-3 space-y-2">
        <p className="text-xs text-muted-foreground">{usesUsername ? 'Förälderns kontaktadress' : 'Spelarens egen e-post'}</p>
        <p className="text-sm font-medium break-all">{data.activation_email || 'E-postadress saknas'}</p>
        <p className="text-xs text-muted-foreground">{usesUsername
          ? 'Spelaren har ingen egen e-post och får ett eget konto med användarnamn och lösenord. Förälderns adress används som kontaktadress.'
          : 'Spelaren loggar in med sin egen e-postadress och sitt lösenord.'}</p>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onManageEmail?.()}>
          Ändra adress eller välj ingen egen e-post
        </Button>
      </div>
      {usesUsername && <details className="text-sm">
        <summary className="cursor-pointer">Användarnamn</summary>
        <div className="pt-2 space-y-2">
          <Label htmlFor={`activation-username-${profileId}`}>Önskat användarnamn (valfritt)</Label>
          <Input id={`activation-username-${profileId}`} value={usernameDraft} disabled={busy} autoCapitalize="none"
            onChange={(event) => setUsernameDraft(event.target.value)} placeholder="Skapas automatiskt om du lämnar tomt" />
        </div>
      </details>}
      <p className="text-sm text-muted-foreground">{copyMode
        ? 'Inget mejl skickas. Kopiera länken och dela den själv, exempelvis via SMS. En tidigare aktiveringslänk ersätts av den nya.'
        : 'Mottagaren väljer lösenord via länken. En tidigare aktiveringslänk ersätts av den nya.'}</p>
      {sentTo && !copyMode && <p role="status" className="text-sm break-all">Aktiveringslänk skickad till <strong>{sentTo}</strong>.</p>}
      {activationUrl && copyMode && <div className="space-y-2">
        <Label htmlFor={`activation-url-${profileId}`}>Aktiveringslänk att dela</Label>
        <Input id={`activation-url-${profileId}`} readOnly value={activationUrl} onFocus={(event) => event.target.select()} />
        <p className="text-xs text-muted-foreground">Dela länken endast med spelaren eller föräldern. Inget mejl har skickats.</p>
        <Button type="button" variant="outline" onClick={async () => {
          try { await navigator.clipboard.writeText(activationUrl); toast.success('Aktiveringslänk kopierad'); }
          catch { toast.error('Markera och kopiera länken från fältet ovan.'); }
        }}>Kopiera länken</Button>
      </div>}
      {!(copyMode && activationUrl) && <Button type="button" disabled={busy || !data.activation_email || !data.activation_login_mode} onClick={() => run(async () => {
        const res = await createRosterActivationLink(teamId, profileId, {
          send_email: !copyMode, expected_email: data.activation_email,
          expected_login_mode: data.activation_login_mode,
          ...(usesUsername && usernameDraft.trim() ? { username: usernameDraft.trim() } : {}),
        });
        if (copyMode) {
          if (!res.data?.url) throw userError('Ingen aktiveringslänk returnerades. Uppdatera och försök igen.');
          if (mounted.current) setActivationUrl(res.data.url);
          try { await navigator.clipboard.writeText(res.data.url); toast.success('Aktiveringslänk kopierad. Inget mejl har skickats.'); }
          catch { toast.info('Länken är skapad. Kopiera den från fältet som visas.'); }
        } else {
          if (res.data?.email_sent !== true) throw userError('Mejlet kunde inte skickas. Uppdatera mottagaren och försök igen.');
          if (mounted.current) setSentTo(res.data.delivery_email);
          toast.success(`Aktiveringslänk skickad till ${res.data.delivery_email}`);
        }
      }, true)}>{busy ? (copyMode ? 'Skapar…' : 'Skickar…') : copyMode ? 'Skapa och kopiera länk' : 'Skicka aktiveringslänk'}</Button>}
    </>}
    <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => { setActivationUrl(''); setSentTo(''); run(async () => {}); }}>Uppdatera uppgifter</Button>
  </section>;

  return <section className="space-y-4 rounded-lg border border-border p-3" data-testid="account-email-panel">
    <div>
      {showHeading && <h3 className="font-semibold">E-post och inloggning</h3>}
      <p className="text-xs text-muted-foreground">{data.has_account ? 'Aktivt konto · samma inloggning i alla lag' : 'Kontot är ännu inte aktiverat'}</p>
    </div>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

    {!data.has_account ? <div className="space-y-3">
      <label className="flex gap-2 items-start text-sm">
        <input type="checkbox" checked={activationForm.noOwnEmail} disabled={busy} onChange={(event) => {
          const noOwnEmail = event.target.checked;
          setActivationForm((previous) => ({ ...previous, noOwnEmail,
            contactEmail: noOwnEmail ? (previous.contactEmail || previous.ownEmail) : previous.contactEmail,
            ownEmail: noOwnEmail ? '' : previous.ownEmail }));
        }} />
        Spelaren har ingen egen e-post – använder användarnamn
      </label>
      {activationForm.noOwnEmail ? <>
        <Label htmlFor={`activation-contact-${profileId}`}>Förälderns kontaktadress</Label>
        <Input id={`activation-contact-${profileId}`} type="email" value={activationForm.contactEmail} disabled={busy}
          onChange={(event) => setActivationForm((previous) => ({ ...previous, contactEmail: event.target.value }))} />
        <p className="text-xs text-muted-foreground">Spelaren får ett eget konto med användarnamn och lösenord. Adressen är en kontaktadress och ändrar inte förälderns konto.</p>
      </> : <>
        <Label htmlFor={`activation-own-${profileId}`}>Spelarens egen e-post</Label>
        <Input id={`activation-own-${profileId}`} type="email" value={activationForm.ownEmail} disabled={busy}
          onChange={(event) => setActivationForm((previous) => ({ ...previous, ownEmail: event.target.value }))} />
        <p className="text-xs text-muted-foreground">Ange en adress som tillhör spelaren. Använd valet ovan om adressen tillhör föräldern.</p>
      </>}
      <p className="text-xs text-muted-foreground">Det sparade valet gäller för skicka, kopiera och massaktivering.</p>
      {unsaved && <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={busy} onClick={() => run(async () => {
          let payload;
          try { payload = activationProfilePayload(activationForm); }
          catch (validationError) { throw userError(validationError.message); }
          await updateRosterPlayer(profileId, payload);
          toast.success('Spelarens aktiveringsuppgifter sparade');
        }, true)}>Spara aktiveringsuppgifter</Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={() => setActivationForm(activationFormFromSummary(data))}>Avbryt ändring</Button>
      </div>}
    </div> : data.login_email ? <div className="space-y-3">
      <Label htmlFor={`login-email-${profileId}`}>{loginDirty ? 'Ny inloggningsadress' : 'Nuvarande inloggningsadress'}</Label>
      <Input id={`login-email-${profileId}`} type="email" value={candidate} disabled={busy}
        onChange={(e) => setCandidate(e.target.value)} />
      {loginDirty && <div className="space-y-3 rounded-md bg-muted/30 p-3">
        <p className="text-sm break-all">Nuvarande adress: <strong>{data.login_email}</strong></p>
        <p className="text-xs">Den nya adressen börjar gälla när verifieringen är klar. Fram tills dess används nuvarande adress för inloggnings- och lösenordslänkar. Bytet gäller kontot i alla lag.</p>
        {data.can_recover ? <>
          <label className="flex gap-2 text-sm items-start"><input type="checkbox" checked={recovery} disabled={busy}
            onChange={(e) => { setRecovery(e.target.checked); setChecked(false); }} />Gamla adressen kan inte användas</label>
          {recovery && <>
            <Label htmlFor={`email-reason-${profileId}`}>Skäl och genomförd identitetskontroll</Label>
            <Input id={`email-reason-${profileId}`} value={reason} maxLength={500} disabled={busy} onChange={(e) => setReason(e.target.value)} />
            <label className="flex gap-2 text-sm items-start"><input type="checkbox" checked={checked} disabled={busy} onChange={(e) => setChecked(e.target.checked)} />Jag har kontrollerat rätt person och konto via en redan betrodd kontaktväg.</label>
            <p className="text-xs text-muted-foreground">Administrativ återställning loggas. Gamla sessioner avslutas när bytet är klart.</p>
          </>}
        </> : <p className="text-xs text-muted-foreground">Kan spelaren inte använda den gamla adressen? Kontakta en plattformsadministratör för identitetskontroll och återställning.</p>}
        <p className="text-xs break-all">Bekräftelse skickas till {recovery ? (candidate.trim() || 'den nya adressen') : `${data.login_email} och ${candidate.trim() || 'den nya adressen'}`}.</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={busy || !candidate.trim() || (recovery && (!checked || reason.trim().length < 10))}
            onClick={requestChange}>Skicka verifiering</Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => {
            setCandidate(data.login_email); setRecovery(false); setChecked(false); setReason('');
          }}>Avbryt ändring</Button>
        </div>
      </div>}
      {data.pending_change && <div role="status" className="rounded border border-amber-500/40 p-3 text-sm space-y-1">
        <p className="font-medium">Adressbyte begärt – väntar på bekräftelse</p>
        <p className="break-all">Ny adress: <strong>{data.pending_change.candidate_email}</strong></p>
        <p className="break-all">Gäller fortfarande: <strong>{data.login_email}</strong></p>
        <p className="text-xs">{data.pending_change.mode === 'recovery'
          ? 'Bekräfta via mejlet till den nya adressen för att slutföra bytet.'
          : 'Bytet slutförs när länkarna i mejlen till både den gamla och den nya adressen har bekräftats.'}</p>
        <p className="text-xs">Inloggnings- och lösenordslänkar skickas till nuvarande adress tills bytet är klart.</p>
        <p className="text-xs">Verifieringen gäller till {new Date(data.pending_change.expires_at).toLocaleString('sv-SE')}.</p>
      </div>}
    </div> : <div className="space-y-3">
      <Label htmlFor={`profile-email-${profileId}`}>Spelarens egen e-post</Label>
      <Input id={`profile-email-${profileId}`} type="email" value={ownEmail} disabled={busy}
        onChange={(e) => setOwnEmail(e.target.value)} placeholder="Lämna tomt om spelaren saknar egen adress" />
      <p className="text-xs text-muted-foreground">{data.has_account
        ? 'Kontot använder användarnamn. Spara spelarens egen adress här och verifiera den för att även kunna logga in med e-post.'
        : 'Aktivering skickas hit. Saknas egen adress används förälderns kontaktadress och spelaren får ett eget användarnamn.'}</p>
      {data.parent_email && <p className="text-xs break-all">Förälderns kontaktadress: {data.parent_email}</p>}
      {dirty && <Button type="button" disabled={busy} onClick={() => run(async () => {
        if (ownEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownEmail.trim())) throw userError('Ange en giltig egen e-postadress eller lämna fältet tomt.');
        await updateRosterPlayer(profileId, { child_email: ownEmail.trim().toLowerCase() });
        toast.success('Spelarens e-post sparad');
      }, true)}>Spara e-postadress</Button>}
      {dirty && <Button type="button" variant="ghost" disabled={busy} onClick={() => setOwnEmail(data.profile_email || '')}>Avbryt ändring</Button>}
      {data.has_account && !dirty && data.profile_email && <>
        <p className="text-xs break-all">Verifieringen skickas till {data.profile_email}.</p>
        <Button type="button" disabled={busy} onClick={() => run(async () => {
          const res = await requestVerifyLoginEmail(teamId, profileId);
          toast.success(res.data?.status === 'already_linked' ? 'Adressen är redan kopplad' : `Verifiering skickad till ${res.data?.child_email}`);
        }, true)}>Verifiera inloggningsadress</Button>
      </>}
    </div>}

    {mode === 'activation-email' && !data.has_account ? null : !data.has_account ? <div className="space-y-2 border-t pt-3">
      <p className="text-sm break-all">Aktiveringslänken skickas till: <strong>{data.activation_email || 'Adress saknas'}</strong></p>
      {unsaved && <p className="text-xs text-muted-foreground">Spara aktiveringsuppgifterna före utskicket.</p>}
      <Button type="button" disabled={busy || unsaved || !data.activation_email || !data.activation_login_mode} onClick={() => run(async () => {
        const res = await createRosterActivationLink(teamId, profileId, { send_email: true, expected_email: data.activation_email, expected_login_mode: data.activation_login_mode });
        if (res.data?.email_sent !== true) throw userError('Mejlet kunde inte skickas. Uppdatera uppgifterna och försök igen.');
        toast.success(`Aktiveringslänk skickad till ${res.data.delivery_email}`);
      }, true)}>Skicka aktiveringslänk</Button>
    </div> : <details className="border-t pt-3">
      <summary className="cursor-pointer text-sm font-medium">Hjälp med inloggning</summary>
      <div className="space-y-3 pt-3 text-sm">
        {data.username && <p className="break-all">Användarnamn: {data.username}</p>}
        <p className="break-all">Länkar skickas till: <strong>{data.reset_email || 'Adress saknas'}</strong></p>
        {!data.login_email && data.reset_email && <p className="text-xs text-muted-foreground">Detta är kontaktadressen som är sparad på kontot.</p>}
        {data.pending_change && <p className="text-xs text-muted-foreground">Adressbytet väntar på bekräftelse. Länkar skickas till adressen ovan tills bytet är klart.</p>}
        {unsaved && <p className="text-xs text-muted-foreground">Slutför eller avbryt adressändringen ovan innan du skickar en länk.</p>}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={busy || !data.reset_email || unsaved} onClick={() => run(async () => {
            const res = await sendPlayerLoginLink(teamId, profileId, data.reset_email);
            if (res.data?.email_delivered !== true) throw userError('Inloggningsmejlet kunde inte skickas.');
            toast.success(`Inloggningslänk skickad till ${res.data.sent_to}`);
          })}>Skicka inloggningslänk</Button>
          <Button type="button" variant="outline" disabled={busy || !data.reset_email || unsaved} onClick={() => run(async () => {
            const res = await sendPlayerResetLink(teamId, profileId, data.reset_email);
            if (res.data?.email_delivered !== true) throw userError('Återställningsmejlet kunde inte skickas.');
            toast.success(`Återställningslänk skickad till ${res.data.sent_to}`);
          })}>Återställ lösenord</Button>
        </div>
        {(data.login_email || data.username) && <Button type="button" variant="ghost" size="sm" onClick={async () => {
          try { await navigator.clipboard.writeText(data.login_email || data.username); toast.success('Inloggning kopierad'); }
          catch { toast.error('Kunde inte kopiera. Markera och kopiera uppgiften manuellt.'); }
        }}>Kopiera inloggning</Button>}
      </div>
    </details>}
    <Button type="button" size="sm" variant="ghost" disabled={busy || unsaved} onClick={() => run(async () => {})}>Uppdatera uppgifter</Button>
  </section>;
}
